import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BackspaceOutlinedIcon from "@mui/icons-material/BackspaceOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import { useCallback, useEffect, useState } from "react";
import { useAdmin } from "../contexts/useAdmin";

// Wrong PINs allowed before input is disabled for a while. Only enforced
// here in the UI.
const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 60_000;
const PIN_LENGTH = 4;

const shake = keyframes`
  10%, 90% { transform: translateX(-2px); }
  20%, 80% { transform: translateX(4px); }
  30%, 50%, 70% { transform: translateX(-8px); }
  40%, 60% { transform: translateX(8px); }
`;

// Kept outside the component so closing and reopening Settings doesn't reset
// the lockout.
let savedFailures = 0;
let savedLockedUntil = 0;

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "back"];

interface AdminPinPadProps {
  onClose: () => void;
}

// Full-screen PIN entry shown when Settings opens in a room with an admin PIN,
// like a banking app's passcode screen. Unlocking flips admin_status, and the
// settings screen replaces this.
const AdminPinPad = ({ onClose }: AdminPinPadProps) => {
  const { login } = useAdmin();
  const [pin, setPin] = useState("");
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [shaking, setShaking] = useState(false);
  const [failures, setFailuresState] = useState(savedFailures);
  const [lockedUntil, setLockedUntilState] = useState(savedLockedUntil);
  const setFailures = (value: number) => {
    savedFailures = value;
    setFailuresState(value);
  };
  const setLockedUntil = (value: number) => {
    savedLockedUntil = value;
    setLockedUntilState(value);
  };
  const [now, setNow] = useState(Date.now());
  const locked = now < lockedUntil;

  // Tick once a second during a lockout for the countdown.
  useEffect(() => {
    if (!locked) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [locked]);

  const submit = useCallback(
    async (value: string) => {
      setChecking(true);
      const ok = await login(value);
      setChecking(false);
      if (ok) return;

      setPin("");
      setShaking(true);
      const count = failures + 1;
      if (count >= MAX_ATTEMPTS) {
        setFailures(0);
        setLockedUntil(Date.now() + LOCKOUT_MS);
        setNow(Date.now());
        setError("");
      } else {
        setFailures(count);
        setError("Wrong PIN");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [failures, login]
  );

  const press = useCallback(
    (key: string) => {
      if (checking || locked) return;
      if (key === "back") {
        setPin((value) => value.slice(0, -1));
        return;
      }
      if (pin.length >= PIN_LENGTH) return;
      const next = pin + key;
      setPin(next);
      setError("");
      if (next.length === PIN_LENGTH) submit(next);
    },
    [checking, locked, pin, submit]
  );

  // Physical keyboard on desktop.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (/^\d$/.test(event.key)) press(event.key);
      else if (event.key === "Backspace") press("back");
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [press]);

  const secondsLeft = Math.ceil((lockedUntil - now) / 1000);
  const message = locked
    ? `Too many attempts. Try again in ${secondsLeft}s`
    : error;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        // Above the settings content it covers (same modal).
        zIndex: (theme) => theme.zIndex.modal + 1,
        display: "flex",
        flexDirection: "column",
        background: "#121212",
        color: "#fff",
      }}
    >
      <Box sx={{ p: 1 }}>
        <IconButton color="inherit" aria-label="close" onClick={onClose}>
          <ArrowBackIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          px: 2,
          pb: 4,
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 40, opacity: 0.8 }} />
        <Typography variant="h6">Enter admin PIN</Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2.5,
            animation: shaking ? `${shake} 0.4s` : "none",
          }}
          onAnimationEnd={() => setShaking(false)}
        >
          {Array.from({ length: PIN_LENGTH }, (_, i) => (
            <Box
              key={i}
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                border: "2px solid #fff",
                background: i < pin.length ? "#fff" : "transparent",
                transition: "background 0.1s",
              }}
            />
          ))}
        </Box>

        <Typography
          variant="body2"
          color="error"
          sx={{ minHeight: "1.5em", textAlign: "center" }}
        >
          {message}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 72px)",
            gap: { xs: 2, sm: 2.5 },
            opacity: locked ? 0.4 : 1,
          }}
        >
          {KEYS.map((key, i) =>
            key === "" ? (
              <Box key={i} />
            ) : (
              <ButtonBase
                key={i}
                aria-label={key === "back" ? "delete" : key}
                disabled={checking || locked}
                onClick={() => press(key)}
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  fontSize: 28,
                  fontWeight: 400,
                  background:
                    key === "back" ? "transparent" : "rgba(255,255,255,.08)",
                  "&:active": { background: "rgba(255,255,255,.2)" },
                }}
              >
                {key === "back" ? <BackspaceOutlinedIcon /> : key}
              </ButtonBase>
            )
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPinPad;
